"use client";

import {
  type DetailsHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import classNames from "classnames";

import ChevronIcon from "@/components/Icons/ChevronIcon";

import styles from "./ResponsiveAccordion.module.css";

type SharedContainerProps = {
  label: ReactNode;
  children: ReactNode;
};

const DetailsContainer = ({
  label,
  className,
  children,
  ...props
}: SharedContainerProps & DetailsHTMLAttributes<HTMLDetailsElement>) => (
  <details
    data-testid="responsive-accordion-details"
    className={classNames(styles.accordionV2, className)}
    {...props}
  >
    <summary
      data-testid="responsive-accordion-summary"
      className={styles.label}
    >
      {label}{" "}
      <ChevronIcon className={classNames("marker", styles.accordionMarker)} />
    </summary>
    {children}
  </details>
);

const DivContainer = ({
  label,
  className,
  children,
  ...props
}: SharedContainerProps & HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid="responsive-accordion-div"
    className={classNames(styles.accordionV2, className)}
    {...props}
  >
    <div className={styles.label}>{label}</div>
    {children}
  </div>
);

type ResponsiveAccordionProps = SharedContainerProps & {
  /** Whether the accordion should permanently expand on large screens */
  unfurlOnLarge?: boolean;
} & DetailsHTMLAttributes<HTMLDetailsElement>;

export const ResponsiveAccordion = ({
  unfurlOnLarge = true,
  ...props
}: ResponsiveAccordionProps) =>
  !unfurlOnLarge ? (
    <DetailsContainer {...props} />
  ) : (
    <>
      <DivContainer
        className={classNames("hidden lg:block", props.className)}
        label={props.label}
      >
        {props.children}
      </DivContainer>
      <DetailsContainer
        className={classNames("lg:hidden block", props.className)}
        {...props}
      />
    </>
  );

export default ResponsiveAccordion;
